/**
 * L1 §3 — SLA tiers. The tier is encoded in the job name (`HC-SLA01…SLA20`),
 * so today it is a naming convention nobody measures; the intent lives in a
 * hand-maintained yearly workbook (concept §2.4). This panel turns the
 * convention into a measurement: how many jobs sit in each tier, and how many
 * of them are currently meeting it.
 *
 * Form (design rules §2): comparing magnitudes across categories, with a
 * composition inside each — a horizontal stacked bar, tiers on the category
 * axis, outcome as the stack. Status colours only, never the categorical ramp,
 * because "failed" is a status and not a series. Legend is mandatory at ≥2
 * series (§6), and each segment drills into the filtered job list (§4.1).
 *
 * Jobs whose name encodes no tier are **not** dropped — they are grouped as
 * "Untiered" and counted, because a job nobody assigned an SLA to is itself a
 * finding rather than a rounding error.
 *
 * The oldest restore point per tier rides along in the table twin: the tier
 * *is* a recovery-time promise, and the bar can only show whether jobs are
 * currently green, not whether the promise is being kept.
 */

import { Chart } from "@norican/dataviz";
import { Icon } from "@norican/icons";
import { EmptyState } from "@norican/react";

import { ChartCard } from "@/components/board";
import type { BackupJobRow } from "@/lib/api";
import { formatCount, formatRelativeTime } from "@/lib/charts/config";
import { useDrillNavigate } from "@/lib/drill";

import { BACKUP_JOBS_PATH, RESULT_PARAM, SITE_PARAM, SLA_PARAM, SOURCE_PARAM } from "./params";
import { statusStyle } from "./status";

/** The outcomes worth stacking. `disabled` is out of the bar and counted aside. */
const STACKED = [
  "success",
  "warning",
  "failed",
  "missed",
  "never-run",
  "running",
  "unknown",
] as const;

/** The stacked subset of the job statuses — everything except `disabled`. */
type StackedStatus = (typeof STACKED)[number];

const SLA_CONFIG = Object.fromEntries(
  STACKED.map((status) => [
    status,
    { label: statusStyle(status).short, color: statusStyle(status).color },
  ]),
);

interface TierRow extends Record<StackedStatus, number> {
  /** `SLA01`…`SLA20`, or the literal `Untiered`. */
  tier: string;
  jobs: number;
  disabled: number;
  /** Oldest `last_success` across the tier's enabled jobs, null when none. */
  oldestRestorePoint: string | null;
  /** Enabled jobs in the tier with no successful run on record. */
  withoutRestorePoint: number;
}

const UNTIERED = "Untiered";

interface SlaSectionProps {
  jobs: BackupJobRow[];
  collectedAt: string | null;
  sourceIds: string[];
  siteIds: string[];
}

export function SlaSection({ jobs, collectedAt, sourceIds, siteIds }: SlaSectionProps) {
  const navigateDrill = useDrillNavigate();

  const scoped = jobs.filter(
    (job) =>
      (sourceIds.length === 0 || sourceIds.includes(job.source)) &&
      (siteIds.length === 0 || (job.site != null && siteIds.includes(job.site))),
  );

  const byTier = new Map<string, TierRow>();
  for (const job of scoped) {
    const tier = job.sla ?? UNTIERED;
    let row = byTier.get(tier);
    if (!row) {
      row = {
        tier,
        jobs: 0,
        disabled: 0,
        oldestRestorePoint: null,
        withoutRestorePoint: 0,
        success: 0,
        warning: 0,
        failed: 0,
        missed: 0,
        "never-run": 0,
        running: 0,
        unknown: 0,
      };
      byTier.set(tier, row);
    }
    row.jobs += 1;
    if (job.status === "disabled") {
      row.disabled += 1;
      continue;
    }
    row[job.status] += 1;
    if (job.last_success == null) {
      row.withoutRestorePoint += 1;
    } else if (row.oldestRestorePoint == null || job.last_success < row.oldestRestorePoint) {
      row.oldestRestorePoint = job.last_success;
    }
  }

  // SLA01 first, Untiered last — the tier number is the priority, and the jobs
  // nobody tiered belong at the bottom of the reading order, not hidden.
  const rows = [...byTier.values()].sort((left, right) => {
    if (left.tier === UNTIERED) {
      return 1;
    }
    if (right.tier === UNTIERED) {
      return -1;
    }
    return left.tier.localeCompare(right.tier);
  });

  const drillParams = {
    ...(sourceIds.length > 0 ? { [SOURCE_PARAM]: sourceIds } : {}),
    ...(siteIds.length > 0 ? { [SITE_PARAM]: siteIds } : {}),
  };

  const untieredJobs = byTier.get(UNTIERED)?.jobs ?? 0;
  const tierCount = untieredJobs > 0 ? rows.length - 1 : rows.length;

  return (
    <section id="backup-sla" className="scroll-mt-6">
      <ChartCard<TierRow>
        title="SLA tiers"
        caption={
          untieredJobs > 0
            ? `${formatCount(tierCount)} tiers · ${formatCount(untieredJobs)} jobs carry no tier in their name`
            : `${formatCount(tierCount)} tiers measured from the job names`
        }
        since={collectedAt}
        tableTwin={{
          id: "backup-sla-tiers",
          ariaLabel: "Jobs and outcomes per SLA tier",
          data: rows,
          getRowId: (row) => row.tier,
          columns: [
            { id: "tier", header: "Tier", accessor: (row) => row.tier },
            { id: "jobs", header: "Jobs", accessor: (row) => row.jobs, align: "end" },
            { id: "ok", header: "Succeeded", accessor: (row) => row.success, align: "end" },
            { id: "warn", header: "Warnings", accessor: (row) => row.warning, align: "end" },
            { id: "failed", header: "Failed", accessor: (row) => row.failed, align: "end" },
            { id: "missed", header: "Did not run", accessor: (row) => row.missed, align: "end" },
            {
              id: "never",
              header: "Never ran",
              accessor: (row) => row["never-run"],
              align: "end",
            },
            { id: "disabled", header: "Disabled", accessor: (row) => row.disabled, align: "end" },
            {
              id: "oldest",
              header: "Oldest restore point",
              accessor: (row) => row.oldestRestorePoint ?? "",
              cell: (row) => (
                <span className="text-sm">
                  {row.oldestRestorePoint
                    ? formatRelativeTime(row.oldestRestorePoint)
                    : "No proven backup"}
                </span>
              ),
            },
            {
              id: "norestore",
              header: "No restore point",
              accessor: (row) => row.withoutRestorePoint,
              align: "end",
            },
          ],
        }}
      >
        {rows.length === 0 ? (
          <EmptyState
            size="sm"
            title="No jobs in scope"
            description="Clear the source and site filters above to see every tier."
          />
        ) : (
          <div className="flex flex-col gap-3">
            <Chart.Container
              config={SLA_CONFIG}
              className="h-96"
              aria-label="Jobs per SLA tier, stacked by their latest outcome"
            >
              <Chart.BarChart
                data={rows}
                layout="vertical"
                margin={{ top: 8, right: 12, bottom: 0, left: 8 }}
              >
                <Chart.CartesianGrid {...Chart.gridProps} horizontal={false} />
                <Chart.XAxis {...Chart.axisProps} type="number" allowDecimals={false} />
                <Chart.YAxis {...Chart.axisProps} type="category" dataKey="tier" width={88} />
                <Chart.Tooltip
                  cursor={{ fill: "var(--norican-surface-hover)" }}
                  content={<Chart.TooltipContent />}
                />
                <Chart.Legend content={<Chart.LegendContent />} />
                {STACKED.map((status, index) => (
                  <Chart.Bar
                    {...Chart.seriesDefaults}
                    key={status}
                    dataKey={status}
                    stackId="outcome"
                    fill={`var(--color-${status})`}
                    radius={index === STACKED.length - 1 ? [0, 3, 3, 0] : undefined}
                    cursor="pointer"
                    onClick={(item: { payload?: TierRow }) => {
                      const tier = item.payload?.tier;
                      if (tier) {
                        navigateDrill(BACKUP_JOBS_PATH, {
                          ...drillParams,
                          ...(tier === UNTIERED ? {} : { [SLA_PARAM]: tier }),
                          [RESULT_PARAM]: status,
                        });
                      }
                    }}
                  />
                ))}
              </Chart.BarChart>
            </Chart.Container>

            {/* Outcome is never colour-alone — the legend above is paired with
                icons here, matching the badges in the grids (§3.2). */}
            <div className="text-fg-muted flex flex-wrap justify-center gap-3 text-xs">
              {STACKED.map((status) => {
                const style = statusStyle(status);
                const total = rows.reduce((sum, row) => sum + row[status], 0);
                return (
                  <span key={status} className="inline-flex items-center gap-1.5">
                    <Icon icon={style.icon} size="xs" />
                    {style.short} ({formatCount(total)})
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </ChartCard>
    </section>
  );
}
